import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-white rounded-xl border border-[#D8E6F1] px-4 py-3.5 text-[#0C2A40] placeholder:text-[#93A5B5] focus:border-[#2880B8] focus:ring-2 focus:ring-[#2880B8]/20 focus:outline-none transition-all duration-300 text-sm";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form);
      toast.success("Welcome to Astra.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white" data-testid="signup-page">
      <div className="flex items-center justify-center p-6 md:p-12 order-2 md:order-1">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-[#51667A] hover:text-[#0C2A40] transition-colors mb-12">
            <ArrowLeft size={14} /> Back
          </Link>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-[#0C2A40]">Get a demo</h1>
          <p className="mt-3 text-[#51667A]">Create your Astra workspace in seconds.</p>

          <form onSubmit={submit} className="mt-10 space-y-4">
            <input data-testid="signup-name" required placeholder="Full name" value={form.name} onChange={update("name")} className={inputCls} />
            <input data-testid="signup-email" required type="email" placeholder="Work email" value={form.email} onChange={update("email")} className={inputCls} />
            <input data-testid="signup-company" placeholder="Practice / company" value={form.company} onChange={update("company")} className={inputCls} />
            <input data-testid="signup-password" required type="password" placeholder="Password" value={form.password} onChange={update("password")} className={inputCls} />
            {error && <p data-testid="signup-error" className="text-sm text-red-500 font-mono">{error}</p>}
            <button data-testid="signup-submit" type="submit" disabled={loading} className="w-full py-4 rounded-full bg-[#2880B8] text-white font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-[#1F6EA1] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : (<>Create Workspace <ArrowRight size={16} /></>)}
            </button>
          </form>

          <p className="mt-8 text-sm text-[#51667A]">
            Already have access?{" "}
            <Link to="/login" data-testid="link-login" className="text-[#2880B8] font-medium hover-underline">Log in</Link>
          </p>
        </motion.div>
      </div>

      <div className="relative hidden md:block overflow-hidden bg-[#0C2A40] order-1 md:order-2">
        <img
          src="https://images.unsplash.com/photo-1606857521015-7f9fcf423740?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Astra workspace"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0C2A40] via-[#0C2A40]/70 to-[#0C2A40]/30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 items-end text-right">
          <div className="bg-white rounded-xl px-4 py-3 inline-flex shadow-lg">
            <img src="/astra-logo.png" alt="Astra" className="h-10 w-auto" />
          </div>
          <div>
            <p className="font-display text-4xl font-medium tracking-tighter leading-[0.95] text-white max-w-md">
              One platform. <span className="font-extrabold text-[#9FD0EC]">Zero silos.</span>
            </p>
            <p className="mt-6 flex items-center justify-end gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-[#9FD0EC]"><ShieldCheck size={15} /> 100% HIPAA Certified</p>
          </div>
        </div>
      </div>
    </div>
  );
}
